/* ===== Requests Page (Solicitudes) ===== */

import { useEffect, useState } from 'react'
import type { ServiceRequest, ServiceRequestComment } from '@zetyar/domain'
import { ClipboardList, Plus, Send } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webRequestRepository } from '@/infrastructure/repositories/requests'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

const STATUS_LABEL: Record<string, string> = {
  open: 'Abierta',
  in_progress: 'En proceso',
  closed: 'Cerrada',
}

const STATUS_VARIANT: Record<string, 'neutral' | 'success' | 'danger'> = {
  open: 'neutral',
  in_progress: 'success',
  closed: 'danger',
}

export function RequestsPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  // update_request_status / assign_request (0013_rls_requests.sql) authorize organization_admin
  // OR moderator - members.manage alone (org admin only) would hide this from moderators who can
  // actually perform the action server-side.
  const canManage =
    profile?.isSuperAdmin ||
    permissions.includes('members.manage') ||
    (currentMembership?.roles.includes('moderator') ?? false)
  const { guard, reauthModal } = useReauthGuard()

  const [requests, setRequests] = useState<ServiceRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')

  const [openRequestId, setOpenRequestId] = useState<string | null>(null)
  const [comments, setComments] = useState<ServiceRequestComment[]>([])
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [commentText, setCommentText] = useState('')

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const load = async () => {
    const context = tenantContext()
    if (!context) {
      setRequests([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const result = canManage && webRequestRepository.listForOrganization
        ? await webRequestRepository.listForOrganization(context, { limit: 200 })
        : await webRequestRepository.listMine(context, { limit: 200 })
      setRequests(result)
    } catch (error) {
      console.error('Error al cargar solicitudes:', error)
      setRequests([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    const run = async () => {
      if (!isMounted) return
      await load()
    }
    void run()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user, canManage])

  const openRequest = requests.find((r) => r.id === openRequestId) ?? null

  const loadDetail = async (requestId: string) => {
    const context = tenantContext()
    if (!context) return

    try {
      setIsDetailLoading(true)
      const result = await webRequestRepository.get(context, requestId, 200)
      setComments(result?.comments ?? [])
    } catch (error) {
      console.error('Error al cargar la solicitud:', error)
      setComments([])
    } finally {
      setIsDetailLoading(false)
    }
  }

  const handleOpenRequest = (requestId: string) => {
    setOpenRequestId(requestId)
    void loadDetail(requestId)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context) return

    try {
      await webRequestRepository.create(context, { subject, description })
      await load()
    } catch (error) {
      console.error('Error al crear la solicitud:', error)
    }

    setIsCreateOpen(false)
    setSubject('')
    setDescription('')
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context || !openRequest || !commentText.trim()) return

    try {
      await webRequestRepository.addComment(context, openRequest.id, openRequest.requestedById, commentText.trim())
      setCommentText('')
      await loadDetail(openRequest.id)
    } catch (error) {
      console.error('Error al comentar la solicitud:', error)
    }
  }

  const handleStatusChange = (requestId: string, notifyMembershipId: string, status: ServiceRequest['status']) => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webRequestRepository.updateStatus) return

      try {
        await webRequestRepository.updateStatus(context, requestId, notifyMembershipId, status)
        await load()
        await loadDetail(requestId)
      } catch (error) {
        console.error('Error al cambiar el estado de la solicitud:', error)
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Solicitudes
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            {canManage
              ? `Todas las solicitudes de ${currentOrganization?.name}`
              : 'Tus solicitudes a la administracion'}
          </p>
        </div>
        <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setIsCreateOpen(true)}>
          Nueva solicitud
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="w-5 h-5 text-indigo-600" />
            Solicitudes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
          ) : requests.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No hay solicitudes todavia.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <button
                  key={request.id}
                  type="button"
                  onClick={() => handleOpenRequest(request.id)}
                  className="w-full text-left p-4 rounded-md border border-neutral-200 dark:border-neutral-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{request.subject}</h3>
                      {canManage && request.requestedByDisplayName && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                          De {request.requestedByDisplayName}
                        </p>
                      )}
                    </div>
                    <Badge variant={STATUS_VARIANT[request.status]}>{STATUS_LABEL[request.status]}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Nueva solicitud">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Asunto"
            placeholder="Ej: Problema con acceso al estacionamiento"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <Input
            label="Descripcion"
            placeholder="Describe tu solicitud con el mayor detalle posible"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setIsCreateOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Enviar solicitud
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={openRequest !== null} onClose={() => setOpenRequestId(null)} title={openRequest?.subject ?? ''}>
        {openRequest && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge variant={STATUS_VARIANT[openRequest.status]}>{STATUS_LABEL[openRequest.status]}</Badge>
              {canManage && (
                <div className="flex gap-2">
                  {openRequest.status !== 'in_progress' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange(openRequest.id, openRequest.requestedById, 'in_progress')}
                    >
                      Marcar en proceso
                    </Button>
                  )}
                  {openRequest.status !== 'closed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange(openRequest.id, openRequest.requestedById, 'closed')}
                    >
                      Cerrar
                    </Button>
                  )}
                </div>
              )}
            </div>

            <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
              {openRequest.description}
            </p>

            <div className="border-t border-neutral-100 dark:border-neutral-800 pt-4 space-y-3">
              <h4 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                Respuestas
              </h4>
              {isDetailLoading ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
              ) : comments.length === 0 ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Sin respuestas todavia.</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {comments.map((comment) => (
                    <div key={comment.id} className="text-sm">
                      <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                        {comment.authorDisplayName}
                      </span>
                      <p className="text-neutral-700 dark:text-neutral-300">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleAddComment} className="flex gap-2 pt-2">
                <input
                  className="flex-1 h-10 px-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  placeholder="Escribe una respuesta..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <Button type="submit" variant="primary" size="sm" leftIcon={<Send className="w-3.5 h-3.5" />}>
                  Enviar
                </Button>
              </form>
            </div>
          </div>
        )}
      </Modal>
      {reauthModal}
    </div>
  )
}
